import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Camera } from 'lucide-react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type QRScannerDialogRef = {
  start: () => Promise<void>
  stop: () => void
}

export const QRScannerDialog = forwardRef<QRScannerDialogRef, { onScan: (text: string) => void }>(
  function QRScannerDialog({ onScan }, ref) {
    const { t } = useTranslation()
    const videoRef = useRef<HTMLVideoElement>(null)
    const scannerRef = useRef<InstanceType<typeof import('qr-scanner').default> | null>(null)
    const onScanRef = useRef(onScan)
    const [isOpen, setIsOpen] = useState(false)
    const [error, setError] = useState('')
    const [hasCamera, setHasCamera] = useState(false)

    onScanRef.current = onScan

    const closeAndCleanup = useCallback(() => {
      if (scannerRef.current) {
        scannerRef.current.stop()
        scannerRef.current.destroy()
        scannerRef.current = null
      }
      setIsOpen(false)
      setHasCamera(false)
      setError('')
    }, [])

    useImperativeHandle(ref, () => ({
      start: async () => {
        if (scannerRef.current) return

        try {
          const permissionStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
          })
          permissionStream.getTracks().forEach(t => t.stop())
          setIsOpen(true)
          setError('')

          await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
          await new Promise<void>(resolve => setTimeout(resolve, 50))

          if (!videoRef.current) {
            setIsOpen(false)
            return
          }

          const { default: QrScanner } = await import('qr-scanner')
          const scanner = new QrScanner(
            videoRef.current,
            result => {
              onScanRef.current(result.data)
              closeAndCleanup()
            },
            {
              returnDetailedScanResult: true,
              highlightScanRegion: true,
              highlightCodeOutline: true,
              maxScansPerSecond: 5,
              preferredCamera: 'environment',
            },
          )

          scannerRef.current = scanner
          await scanner.start()

          if (scannerRef.current === scanner) {
            setHasCamera(true)
          }
        } catch (err) {
          console.error('QR Scanner error:', err)
          const message = err instanceof Error ? err.message : String(err)
          if (message.includes('NotAllowedError') || message.includes('Permission denied')) {
            setError(t('common.qrScanner.cameraError'))
          } else if (message.includes('NotFoundError') || message.includes('DevicesNotFoundError')) {
            setError(t('common.qrScanner.cameraError'))
          } else {
            setError(t('common.qrScanner.cameraError') + (message ? ` (${message})` : ''))
          }
          setIsOpen(true)
          setHasCamera(false)
        }
      },

      stop: closeAndCleanup,
    }))

    useEffect(() => {
      return () => {
        closeAndCleanup()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open) closeAndCleanup()
        }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="size-4" />
              {t('common.qrScanner.title')}
            </DialogTitle>
            <DialogDescription className="sr-only"></DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4">
            {error ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-center">
                <Camera className="size-8 text-destructive" />
                <p className="text-sm font-medium text-destructive">{error}</p>
              </div>
            ) : (
              <div className="relative w-full overflow-hidden rounded-lg bg-black">
                <video ref={videoRef} className="w-full" style={{ minHeight: '240px', objectFit: 'cover' }} />
                {!hasCamera && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <div className="size-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <p className="text-sm text-white/70">{t('common.loading')}</p>
                  </div>
                )}
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground">{t('common.qrScanner.hint')}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" className="text-xs" onClick={closeAndCleanup}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  },
)
